import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { useInitiativeStore } from './initiativeStore';

/**
 * Encounter Store
 *
 * Single source of truth for combat encounters. Coordinates initiative,
 * turn order, and participant lifecycle. Bridges to initiativeStore
 * (read-only push for UI panel) and replaces the scattered combat state
 * that was split across combatStore + initiativeStore + mobStore.
 *
 * DB: machina_combat_state (participants jsonb column)
 */

// =============================================================================
// TYPES
// =============================================================================

export interface Participant {
  id: string;           // character UUID or mob UUID
  type: 'character' | 'mob';
  name: string;
  initiative: number;
  dexMod: number;
  isAlive: boolean;
  hasActed: boolean;
}

export interface Encounter {
  id: string;           // DB row id (machina_combat_state.id)
  sceneId: string;
  campaignId: string;
  status: 'setup' | 'active' | 'completed';
  participants: Map<string, Participant>;
  currentTurnId: string | null;
  round: number;
  mapId: string | null;
}

interface DbCombatState {
  id: string;
  scene_id: string;
  round_number: number;
  current_turn_index: number;
  is_active: boolean;
  initiative_order: Array<{ characterId: string; initiative: number; dexMod?: number }>;
  participants: SerializedParticipant[];
}

interface SerializedParticipant {
  id: string;
  type: 'character' | 'mob';
  name: string;
  initiative: number;
  dexMod: number;
  isAlive: boolean;
  hasActed: boolean;
}

// =============================================================================
// STATE
// =============================================================================

interface EncounterState {
  activeEncounter: Encounter | null;

  // Supabase sync
  realtimeCleanup: (() => void) | null;
  _insertInFlight: boolean;   // guards against duplicate INSERTs
  _needsResync: boolean;      // queues a re-sync after INSERT completes
  _lastLocalMutationAt: number; // timestamp of last local participant change — used to skip stale realtime echoes

  // Actions — lifecycle
  startEncounter: (sceneId: string, campaignId: string) => void;
  addParticipant: (id: string, type: 'character' | 'mob', name: string, initiative: number, dexMod: number) => void;
  removeParticipant: (id: string) => void;
  beginCombat: () => void;
  nextTurn: () => void;
  prevTurn: () => void;
  markDead: (id: string) => void;
  markAlive: (id: string) => void;
  endEncounter: () => Promise<void>;

  // Actions — updates
  setParticipantInitiative: (id: string, initiative: number) => void;
  updateParticipantName: (id: string, name: string) => void;

  // Queries
  getParticipantsSorted: () => Participant[];
  getCurrentParticipant: () => Participant | null;
  isInCombat: () => boolean;

  // Supabase sync
  loadFromSupabase: (sceneId: string, campaignId: string) => Promise<void>;
  syncToSupabase: () => Promise<void>;
  subscribeToRealtime: (sceneId: string) => void;
  unsubscribeFromRealtime: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function sortParticipants(participants: Participant[]): Participant[] {
  return [...participants].sort((a, b) => {
    if (b.initiative !== a.initiative) return b.initiative - a.initiative;
    return b.dexMod - a.dexMod;
  });
}

function pushToInitiativeStore(encounter: Encounter | null) {
  const initStore = useInitiativeStore.getState();

  if (!encounter) {
    initStore.clearAllEntries();
    return;
  }

  const sorted = sortParticipants(Array.from(encounter.participants.values()));

  // Sync entries — add missing, update changed
  const existingIds = new Set(initStore.entries.map(e => e.id));
  const targetIds = new Set<string>();

  for (const p of sorted) {
    const panelId = p.type === 'character' ? `char-${p.id}` : p.id;
    targetIds.add(panelId);

    const existing = initStore.entries.find(e => e.id === panelId);
    if (existing) {
      if (existing.initiative !== p.initiative) {
        initStore.setInitiative(panelId, p.initiative);
      }
    } else {
      initStore.addEntry({
        id: panelId,
        name: p.name,
        initiative: p.initiative,
        dexMod: p.dexMod,
        type: p.type,
        characterId: p.type === 'character' ? p.id : undefined,
        conditions: [],
      });
    }
  }

  // Remove mob entries that are no longer participants.
  // Character entries are managed by InitiativePanel's auto-populate effect
  // and must NOT be removed here (otherwise stale DB loads or partial
  // encounter creation wipes the party from the initiative panel).
  for (const id of existingIds) {
    if (!targetIds.has(id)) {
      const entry = initStore.entries.find(e => e.id === id);
      if (entry && entry.type === 'mob') {
        initStore.removeEntry(id);
      }
    }
  }

  // Sync combat state
  if (encounter.status === 'active' && !initStore.isInCombat) {
    initStore.startCombat();
  }

  // Sync current turn
  if (encounter.currentTurnId) {
    const p = encounter.participants.get(encounter.currentTurnId);
    if (p) {
      const panelId = p.type === 'character' ? `char-${p.id}` : p.id;
      const idx = initStore.entries.findIndex(e => e.id === panelId);
      if (idx >= 0 && idx !== initStore.currentIndex) {
        initStore.setCurrentIndex(idx);
      }
    }
  }
}

function serializeParticipants(encounter: Encounter): SerializedParticipant[] {
  return Array.from(encounter.participants.values());
}

function deserializeParticipants(arr: SerializedParticipant[]): Map<string, Participant> {
  const map = new Map<string, Participant>();
  for (const p of arr) {
    map.set(p.id, p);
  }
  return map;
}

// Also produce the legacy initiative_order array for backward compat
function toLegacyInitiativeOrder(encounter: Encounter): Array<{ characterId: string; initiative: number; dexMod: number }> {
  return Array.from(encounter.participants.values()).map(p => ({
    characterId: p.id,
    initiative: p.initiative,
    dexMod: p.dexMod,
  }));
}

// =============================================================================
// STORE
// =============================================================================

export const useEncounterStore = create<EncounterState>((set, get) => ({
  activeEncounter: null,
  realtimeCleanup: null,
  _insertInFlight: false,
  _needsResync: false,
  _lastLocalMutationAt: 0,

  // ---------------------------------------------------------------------------
  // LIFECYCLE
  // ---------------------------------------------------------------------------

  startEncounter: (sceneId, campaignId) => {
    const encounter: Encounter = {
      id: '', // Will be set on first DB sync
      sceneId,
      campaignId,
      status: 'setup',
      participants: new Map(),
      currentTurnId: null,
      round: 0,
      mapId: null,
    };
    set({ activeEncounter: encounter });
  },

  addParticipant: (id, type, name, initiative, dexMod) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const participant: Participant = {
      id,
      type,
      name,
      initiative,
      dexMod,
      isAlive: true,
      hasActed: false,
    };

    const newParticipants = new Map(activeEncounter.participants);
    newParticipants.set(id, participant);

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
      _lastLocalMutationAt: Date.now(),
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  removeParticipant: (id) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    newParticipants.delete(id);

    // If we removed the current turn holder, advance
    let currentTurnId = activeEncounter.currentTurnId;
    if (currentTurnId === id) {
      const sorted = sortParticipants(Array.from(newParticipants.values()).filter(p => p.isAlive));
      currentTurnId = sorted.length > 0 ? sorted[0].id : null;
    }

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants, currentTurnId },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  beginCombat: () => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const sorted = sortParticipants(Array.from(activeEncounter.participants.values()).filter(p => p.isAlive));
    if (sorted.length === 0) return;

    // Reset hasActed
    const newParticipants = new Map(activeEncounter.participants);
    for (const [id, p] of newParticipants) {
      newParticipants.set(id, { ...p, hasActed: false });
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        status: 'active',
        participants: newParticipants,
        round: 1,
        currentTurnId: sorted[0].id,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  nextTurn: () => {
    const { activeEncounter } = get();
    if (!activeEncounter || activeEncounter.status !== 'active') return;

    const sorted = sortParticipants(
      Array.from(activeEncounter.participants.values()).filter(p => p.isAlive)
    );
    if (sorted.length === 0) return;

    const currentIdx = sorted.findIndex(p => p.id === activeEncounter.currentTurnId);
    const newParticipants = new Map(activeEncounter.participants);

    // Mark current as acted
    if (activeEncounter.currentTurnId) {
      const current = newParticipants.get(activeEncounter.currentTurnId);
      if (current) {
        newParticipants.set(current.id, { ...current, hasActed: true });
      }
    }

    let nextIdx = currentIdx + 1;
    let newRound = activeEncounter.round;

    if (nextIdx >= sorted.length) {
      // New round — reset hasActed
      nextIdx = 0;
      newRound++;
      for (const [id, p] of newParticipants) {
        newParticipants.set(id, { ...p, hasActed: false });
      }
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        participants: newParticipants,
        currentTurnId: sorted[nextIdx].id,
        round: newRound,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  prevTurn: () => {
    const { activeEncounter } = get();
    if (!activeEncounter || activeEncounter.status !== 'active') return;

    const sorted = sortParticipants(
      Array.from(activeEncounter.participants.values()).filter(p => p.isAlive)
    );
    if (sorted.length === 0) return;

    const currentIdx = sorted.findIndex(p => p.id === activeEncounter.currentTurnId);

    let prevIdx = currentIdx - 1;
    let newRound = activeEncounter.round;

    if (prevIdx < 0) {
      prevIdx = sorted.length - 1;
      newRound = Math.max(1, newRound - 1);
    }

    const newParticipants = new Map(activeEncounter.participants);
    // Un-act the previous participant
    const prev = newParticipants.get(sorted[prevIdx].id);
    if (prev) {
      newParticipants.set(prev.id, { ...prev, hasActed: false });
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        participants: newParticipants,
        currentTurnId: sorted[prevIdx].id,
        round: newRound,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  markDead: (id) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, isAlive: false });

    // If dead participant was current turn, advance to next alive in initiative order
    let currentTurnId = activeEncounter.currentTurnId;
    if (currentTurnId === id) {
      const allSorted = sortParticipants(Array.from(newParticipants.values()));
      const deadIdx = allSorted.findIndex(pp => pp.id === id);
      // Walk forward from dead participant's position, wrapping around, to find next alive
      let nextAlive: string | null = null;
      for (let offset = 1; offset < allSorted.length; offset++) {
        const candidate = allSorted[(deadIdx + offset) % allSorted.length];
        if (candidate.isAlive) {
          nextAlive = candidate.id;
          break;
        }
      }
      currentTurnId = nextAlive;
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        participants: newParticipants,
        currentTurnId,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  markAlive: (id) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, isAlive: true });

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  endEncounter: async () => {
    const { activeEncounter } = get();

    // Clear initiative store
    useInitiativeStore.getState().clearAllEntries();

    if (activeEncounter) {
      // Sync completed state to DB, then clear
      set({
        activeEncounter: { ...activeEncounter, status: 'completed' },
      });
      await get().syncToSupabase();
    }
    set({ activeEncounter: null });
  },

  // ---------------------------------------------------------------------------
  // UPDATES
  // ---------------------------------------------------------------------------

  setParticipantInitiative: (id, initiative) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, initiative });

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
      _lastLocalMutationAt: Date.now(),
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  updateParticipantName: (id, name) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, name });

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
    });

    pushToInitiativeStore(get().activeEncounter);
  },

  // ---------------------------------------------------------------------------
  // QUERIES
  // ---------------------------------------------------------------------------

  getParticipantsSorted: () => {
    const { activeEncounter } = get();
    if (!activeEncounter) return [];
    return sortParticipants(Array.from(activeEncounter.participants.values()));
  },

  getCurrentParticipant: () => {
    const { activeEncounter } = get();
    if (!activeEncounter?.currentTurnId) return null;
    return activeEncounter.participants.get(activeEncounter.currentTurnId) ?? null;
  },

  isInCombat: () => {
    const { activeEncounter } = get();
    return activeEncounter?.status === 'active';
  },

  // ---------------------------------------------------------------------------
  // SUPABASE SYNC
  // ---------------------------------------------------------------------------

  loadFromSupabase: async (sceneId, campaignId) => {
    try {
      const { data, error } = await supabase
        .from('machina_combat_state')
        .select('*')
        .eq('scene_id', sceneId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const dbState = data as unknown as DbCombatState;

        // If the encounter is not active, it's a stale record from a previous battle.
        // Don't populate initiative with ghost mobs — just ignore it.
        if (!dbState.is_active) {
          console.log('[EncounterStore] Skipping inactive encounter (stale combat_state)');
          set({ activeEncounter: null });
          return;
        }

        const participantArr = (dbState.participants ?? []) as SerializedParticipant[];
        const participants = deserializeParticipants(participantArr);

        // Determine current turn from index + sorted participants
        const sorted = sortParticipants(Array.from(participants.values()));
        const turnIdx = Math.min(dbState.current_turn_index, sorted.length - 1);
        const currentTurnId = sorted.length > 0 && turnIdx >= 0 ? sorted[turnIdx].id : null;

        const encounter: Encounter = {
          id: dbState.id,
          sceneId,
          campaignId,
          status: 'active',
          participants,
          currentTurnId,
          round: dbState.round_number,
          mapId: null,
        };

        set({ activeEncounter: encounter });
        pushToInitiativeStore(encounter);
      }
    } catch (err) {
      console.error('[EncounterStore] Failed to load:', err);
    }
  },

  syncToSupabase: async () => {
    const { activeEncounter } = get();
    if (!activeEncounter?.sceneId) return;

    // Guard: if an INSERT is already in flight, queue a re-sync instead of
    // firing a second INSERT (which would create a duplicate row).
    if (!activeEncounter.id && get()._insertInFlight) {
      set({ _needsResync: true });
      return;
    }

    try {
      const sorted = sortParticipants(Array.from(activeEncounter.participants.values()));
      const currentTurnIndex = activeEncounter.currentTurnId
        ? sorted.findIndex(p => p.id === activeEncounter.currentTurnId)
        : 0;

      const payload = {
        scene_id: activeEncounter.sceneId,
        is_active: activeEncounter.status === 'active',
        initiative_order: toLegacyInitiativeOrder(activeEncounter),
        current_turn_index: Math.max(0, currentTurnIndex),
        round_number: activeEncounter.round,
        participants: serializeParticipants(activeEncounter),
        updated_at: new Date().toISOString(),
      };

      if (activeEncounter.id) {
        const { error } = await supabase
          .from('machina_combat_state')
          .update(payload)
          .eq('id', activeEncounter.id);
        if (error) throw error;
      } else {
        set({ _insertInFlight: true });
        try {
          const { data, error } = await supabase
            .from('machina_combat_state')
            .insert(payload)
            .select('id')
            .single();
          if (error) throw error;
          if (data) {
            set({
              activeEncounter: { ...get().activeEncounter!, id: data.id },
            });
          }
        } finally {
          set({ _insertInFlight: false });
        }

        // Flush any mutations that arrived while the INSERT was in flight
        if (get()._needsResync) {
          set({ _needsResync: false });
          get().syncToSupabase();
        }
      }
    } catch (err) {
      console.error('[EncounterStore] Failed to sync:', err);
    }
  },

  subscribeToRealtime: (sceneId) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `encounter:${sceneId}`,
      table: 'machina_combat_state',
      filter: `scene_id=eq.${sceneId}`,
      event: '*',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const dbState = payload.new as unknown as DbCombatState;

          // Skip stale realtime echoes that arrive shortly after a local mutation.
          // Without this, the INSERT/UPDATE echo from our own write can overwrite
          // participants added locally between the write and the echo arrival.
          const msSinceMutation = Date.now() - get()._lastLocalMutationAt;
          if (msSinceMutation < 2000) {
            return;
          }

          // Ignore inactive encounters — prevents ghost mobs from stale records
          if (!dbState.is_active) {
            set({ activeEncounter: null });
            useInitiativeStore.getState().clearAllEntries();
            return;
          }

          const participantArr = (dbState.participants ?? []) as SerializedParticipant[];
          const participants = deserializeParticipants(participantArr);
          const sorted = sortParticipants(Array.from(participants.values()));
          const turnIdx = Math.min(dbState.current_turn_index, sorted.length - 1);
          const currentTurnId = sorted.length > 0 && turnIdx >= 0 ? sorted[turnIdx].id : null;

          const enc = get().activeEncounter;
          const encounter: Encounter = {
            id: dbState.id,
            sceneId,
            campaignId: enc?.campaignId ?? '',
            status: 'active',
            participants,
            currentTurnId,
            round: dbState.round_number,
            mapId: enc?.mapId ?? null,
          };

          set({ activeEncounter: encounter });
          pushToInitiativeStore(encounter);
        } else if (payload.eventType === 'DELETE') {
          set({ activeEncounter: null });
          useInitiativeStore.getState().clearAllEntries();
        }
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
